import { apiFetch } from './client';
import type { PacksCatalog } from './types';

// Pack activation is per-PROJECT — every catalog read + activation write names the project (slug).
const pq = (projectId: string) => `projectId=${encodeURIComponent(projectId)}`;

export const getPacks = (projectId: string) =>
  apiFetch<PacksCatalog>(`/packs?${pq(projectId)}`);

export const activatePack = (id: string, projectId: string) =>
  apiFetch(`/packs/${id}/activate?${pq(projectId)}`, { method: 'POST' });

export const deactivatePack = (id: string, projectId: string) =>
  apiFetch(`/packs/${id}/deactivate?${pq(projectId)}`, { method: 'POST' });

export const setPackMode = (id: string, mode: 'enforce' | 'monitor', projectId: string) =>
  apiFetch(`/packs/${id}/mode?${pq(projectId)}`, { method: 'PATCH', body: JSON.stringify({ mode }) });

// Phase 1: hot authoring
export interface OverridePatch {
  enabledDetectors?: string[];
  injectionBlockScore?: number;
  systemPrompt?: string;
}
export const setPackOverride = (id: string, patch: OverridePatch) =>
  apiFetch(`/packs/${id}/override`, { method: 'PATCH', body: JSON.stringify(patch) });

export const clearPackOverride = (id: string) =>
  apiFetch(`/packs/${id}/override`, { method: 'DELETE' });

// Phase 3: custom rules
export interface CreateCustomRule {
  name: string;
  family: string;
  pattern: string;
  mask?: string;
  direction?: string[];
}
export const createCustomRule = (id: string, rule: CreateCustomRule) =>
  apiFetch(`/packs/${id}/rules`, { method: 'POST', body: JSON.stringify(rule) });

export const updateCustomRule = (ruleId: string, patch: { mode?: 'enforce' | 'monitor'; enabled?: boolean }) =>
  apiFetch(`/packs/rules/${ruleId}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const deleteCustomRule = (ruleId: string) =>
  apiFetch(`/packs/rules/${ruleId}`, { method: 'DELETE' });

// Phase 2: bundle revisions / rollback
export interface BundleRevisionSummary {
  version: string;
  active: boolean;
  createdBy: string | null;
  createdAt: string;
}
export const getRevisions = () => apiFetch<BundleRevisionSummary[]>('/packs/revisions');
export const rollbackRevision = (version: string) =>
  apiFetch(`/packs/revisions/${version}/rollback`, { method: 'POST' });
export const unpinRevision = () => apiFetch('/packs/revisions/unpin', { method: 'POST' });
